<template>
	<v-container
		fluid
		class="cleaning-work-plan pa-4 pa-md-6"
		:class="{
			'cleaning-work-plan--dark': $vuetify.theme.dark,
			'cleaning-work-plan--locale-rtl': isRTL,
		}"
	>
		<!-- 1. Plan settings -->
		<v-card outlined class="cleaning-work-plan__card">
			<v-card-title class="subtitle-1 font-weight-medium pb-2">
				{{ $t('cleaning_work_plan_settings') }}
			</v-card-title>
			<v-card-text class="pt-0">
				<v-row dense align="center">
					<v-col cols="12" sm="6" md="3">
						<v-text-field
							v-model.number="defaultVisitsPerClean"
							type="number"
							min="1"
							step="1"
							:label="$t('cleaning_work_plan_default_n')"
							class="cleaning-work-plan__setting-field"
							dense
							outlined
							hide-details="auto"
						/>
					</v-col>
					<v-col cols="12" sm="6" md="3">
						<v-text-field
							v-model="desiredEmployeeCount"
							type="number"
							min="1"
							step="1"
							clearable
							:label="$t('cleaning_work_plan_desired_employees')"
							class="cleaning-work-plan__setting-field"
							dense
							outlined
							hide-details="auto"
						/>
					</v-col>
					<v-col cols="12" sm="6" md="3">
						<v-text-field
							v-model="costPerHour"
							type="number"
							min="0"
							step="0.01"
							clearable
							:label="$t('cleaning_work_plan_cost_per_hour')"
							class="cleaning-work-plan__setting-field"
							dense
							outlined
							hide-details="auto"
						/>
					</v-col>
					<v-col cols="12" sm="6" md="3">
						<v-select
							:value="selectedBuildings"
							:items="buildingOptions"
							:label="$t('building')"
							multiple
							chips
							small-chips
							deletable-chips
							clearable
							dense
							outlined
							hide-details="auto"
							class="cleaning-work-plan__setting-field"
							@input="onBuildingsInput"
						/>
					</v-col>
					<v-col cols="12" sm="6" md="3">
						<v-text-field
							v-model="avgMinutesPerClean"
							type="number"
							min="0.01"
							step="1"
							clearable
							:label="$t('cleaning_work_plan_avg_minutes')"
							class="cleaning-work-plan__setting-field"
							dense
							outlined
							hide-details="auto"
						/>
					</v-col>
					</v-row>
			</v-card-text>
			<v-divider />
			<v-card-actions class="px-4 py-3 cleaning-work-plan__actions">
				<v-btn
					class="cleaning-work-plan__action-btn cleaning-work-plan__btn-generate"
					:loading="loading"
					:disabled="!canRun"
					@click="run"
				>
					<v-icon left small class="cleaning-work-plan__btn-generate-icon">{{ icon.refresh }}</v-icon>
					{{ $t('cleaning_work_plan_run') }}
				</v-btn>
				<v-btn
					class="cleaning-work-plan__action-btn"
					outlined
					color="primary"
					:disabled="!result || !result.rooms || !result.rooms.length"
					@click="exportCsv"
				>
					<v-icon left small>{{ icon.download }}</v-icon>
					{{ $t('cleaning_work_plan_export_csv') }}
				</v-btn>
				<v-spacer />
				<v-btn
					class="cleaning-work-plan__btn-ai"
					depressed
					:loading="aiAllocationLoading"
					:disabled="!canRunAi || aiAllocationLoading"
					@click="runAiAllocation"
				>
					{{ $t('cleaning_work_plan_ai_allocate') }}
				</v-btn>
			</v-card-actions>
		</v-card>

		<v-expand-transition>
			<v-sheet v-if="aiAllocationLoading" outlined rounded class="cleaning-work-plan__ai-status pa-3 mb-4">
				<div class="d-flex align-center flex-wrap cleaning-work-plan__ai-status-head">
					<v-icon small color="primary" class="mr-2">{{ icon.sync }}</v-icon>
					<span class="text-caption text--secondary">{{ $t('cleaning_work_plan_ai_status_elapsed', { s: aiAllocationElapsedSeconds }) }}</span>
				</div>
				<p class="text-body-2 font-weight-medium mb-1 mt-2">{{ aiAllocationStatusMessage }}</p>
				<p class="text-caption text--secondary mb-3">{{ $t('cleaning_work_plan_ai_status_hint') }}</p>
				<v-progress-linear indeterminate height="3" color="primary" rounded class="mb-0" />
			</v-sheet>
		</v-expand-transition>

		<v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4 rounded" height="4" />

		<v-alert v-if="errorMessage" type="error" dense outlined prominent class="mb-6">
			{{ errorMessage }}
		</v-alert>

		<v-alert
			v-if="result && (!result.rooms || !result.rooms.length)"
			type="warning"
			dense
			outlined
			prominent
			class="mb-6"
		>
			{{ $t('cleaning_work_plan_no_rooms') }}
		</v-alert>

		<!-- 2. Summary -->
		<template v-if="result && result.rooms && result.rooms.length">
			<v-card outlined class="cleaning-work-plan__card cleaning-work-plan__summary-card">
				<v-card-title class="subtitle-1 font-weight-medium">
					{{ $t('cleaning_work_plan_summary') }}
				</v-card-title>
				<v-card-text>
					<v-row dense>
						<v-col cols="12" sm="6" md="3">
							<div class="cleaning-work-plan__stat">
								<span class="cleaning-work-plan__stat-label">{{ $t('site_name') }}</span>
								<span class="cleaning-work-plan__stat-value text-truncate d-block" :title="summarySiteName">
									{{ summarySiteName }}
								</span>
							</div>
						</v-col>
						<v-col cols="12" sm="6" md="3">
							<div class="cleaning-work-plan__stat cleaning-work-plan__stat--numeric">
								<span class="cleaning-work-plan__stat-label">{{ $t('cleaning_work_plan_room_count') }}</span>
								<span class="cleaning-work-plan__stat-value">{{ formatNum(result.totals && result.totals.roomCount) }}</span>
							</div>
						</v-col>
						<v-col cols="12" sm="6" md="3">
							<div class="cleaning-work-plan__stat">
								<span class="cleaning-work-plan__stat-label">{{ $t('cleaning_work_plan_peak_day') }}</span>
								<span
									class="cleaning-work-plan__stat-value text-truncate d-block"
									:title="peakDayLabel"
								>
									{{ peakDayLabel }}
								</span>
							</div>
						</v-col>
						<v-col cols="12" sm="6" md="3">
							<div class="cleaning-work-plan__stat">
								<span class="cleaning-work-plan__stat-label">{{
									$t('cleaning_work_plan_summary_filter_period')
								}}</span>
								<span class="cleaning-work-plan__stat-value text-truncate d-block" :title="periodLabel">
									{{ periodLabel }}
								</span>
							</div>
						</v-col>
						<v-col
							v-if="result.meta && result.meta.buildings && result.meta.buildings.length"
							cols="12"
							sm="6"
							md="3"
						>
							<div class="cleaning-work-plan__stat">
								<span class="cleaning-work-plan__stat-label">{{ $t('building') }}</span>
								<span
									class="cleaning-work-plan__stat-value cleaning-work-plan__stat-value--wrap d-block"
									:title="summaryBuildingsLabel"
								>
									{{ summaryBuildingsLabel }}
								</span>
							</div>
						</v-col>
						<v-col
							v-if="result.meta && result.meta.shiftIds && result.meta.shiftIds.length"
							cols="12"
							sm="6"
							md="3"
						>
							<div class="cleaning-work-plan__stat">
								<span class="cleaning-work-plan__stat-label">{{ $t('shifts') }}</span>
								<span
									class="cleaning-work-plan__stat-value cleaning-work-plan__stat-value--wrap d-block"
									:title="summaryShiftNamesLabel"
								>
									{{ summaryShiftNamesLabel }}
								</span>
							</div>
						</v-col>
						<v-col cols="12" sm="6" md="3">
							<div class="cleaning-work-plan__stat cleaning-work-plan__stat--numeric">
								<span class="cleaning-work-plan__stat-label">{{ $t('cleaning_work_plan_summary_total_visits') }}</span>
								<span class="cleaning-work-plan__stat-value">{{ formatNum(result.totals && result.totals.totalVisits) }}</span>
							</div>
						</v-col>
						<v-col cols="12" sm="6" md="3">
							<div class="cleaning-work-plan__stat cleaning-work-plan__stat--numeric">
								<span class="cleaning-work-plan__stat-label">{{ $t('cleaning_work_plan_summary_total_arrivals') }}</span>
								<span class="cleaning-work-plan__stat-value">{{
									formatNum(result.totals && result.totals.totalArrivals)
								}}</span>
							</div>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</template>

		<!-- 3. Performance comparison -->
		<v-card outlined class="cleaning-work-plan__card cleaning-work-plan__performance-card">
			<v-card-title class="subtitle-1 font-weight-medium">
				{{ $t('cleaning_work_plan_performance_comparison_table') }}
			</v-card-title>
			<v-card-text class="pt-0">
				<v-alert v-if="!canRun" type="warning" dense outlined prominent class="mb-4">
					{{ $t('cleaning_work_plan_need_filter') }}
				</v-alert>
				<v-simple-table
					v-else-if="performanceComparisonRows.length"
					dense
					class="cleaning-work-plan__performance-table"
				>
					<thead>
						<tr>
							<th>{{ $t('cleaning_work_plan_performance_col_index') }}</th>
							<th>{{ $t('cleaning_work_plan_performance_existing_plan') }}</th>
							<th>{{ $t('cleaning_work_plan_performance_recommended_plan') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in performanceComparisonRows" :key="row.key">
							<td>{{ $t(row.indexKey) }}</td>
							<td class="cleaning-work-plan__num">{{ row.existing }}</td>
							<td class="text--secondary">{{ row.recommended }}</td>
						</tr>
					</tbody>
				</v-simple-table>
				<p v-else class="text-body-2 text--secondary mb-0">
					{{ $t('cleaning_work_plan_performance_comparison_empty') }}
				</p>
			</v-card-text>
			</v-card>

		<v-card v-if="aiAllocationResult || aiAllocationError" outlined class="cleaning-work-plan__card">
			<v-card-title class="subtitle-1 font-weight-medium">
				{{ $t('cleaning_work_plan_ai_allocation_result') }}
			</v-card-title>
			<v-card-text class="pt-0">
				<v-alert v-if="aiAllocationError" type="error" dense outlined>
					{{ aiAllocationError }}
				</v-alert>
				<v-data-table
					v-if="aiAllocationResult && aiAllocationResult.allocations"
					:headers="aiAllocationHeaders"
					:items="aiAllocationResult.allocations"
					:items-per-page="-1"
					:footer-props="footerProps"
					class="cleaning-work-plan__data-table cleaning-work-plan__data-table--cleaners"
					:header-props="{ sortIcon: icon.sort }"
					mobile-breakpoint="0"
					dense
				>
					<template #item.cleanerNumber="{ item }">
						<span class="cleaning-work-plan__num">{{ item.cleanerNumber }}</span>
					</template>
					<template #item.floorsAssigned="{ item }">
						<span class="cleaning-work-plan__floors-cell">{{ item.floorsAssigned }}</span>
					</template>
					<template #item.numberOfFloors="{ item }">
						<span class="cleaning-work-plan__num">{{ formatNum(item.numberOfFloors) }}</span>
					</template>
					<template #item.numberOfRooms="{ item }">
						<span class="cleaning-work-plan__num">{{ formatNum(item.numberOfRooms) }}</span>
					</template>
					<template #item.visits="{ item }">
						<span class="cleaning-work-plan__num">{{ formatNum(item.visits) }}</span>
					</template>
					<template #item.arrivals="{ item }">
						<span class="cleaning-work-plan__num">{{ formatNum(item.arrivals) }}</span>
					</template>
					<template #item.ratio="{ item }">
						<span class="cleaning-work-plan__num">{{ formatRatio(item.ratio) }}</span>
					</template>
				</v-data-table>
			</v-card-text>
		</v-card>

		<!-- 4. Cleaners by rooms (collapsed by default) -->
		<v-card
			v-if="canRun"
			outlined
			class="cleaning-work-plan__card cleaning-work-plan__card--collapsible pa-0"
		>
			<v-expansion-panels v-model="cleanersByRoomsExpanded" flat multiple tile>
				<v-expansion-panel>
					<v-expansion-panel-header class="subtitle-1 font-weight-medium">
						{{ $t('cleaning_work_plan_cleaners_connection_table') }}
					</v-expansion-panel-header>
					<v-expansion-panel-content class="pt-0 px-4 pb-4">
						<v-progress-linear
							v-if="cleanerConnectionLoading && !cleanerConnectionPayload"
							indeterminate
							color="primary"
							class="mb-2 rounded"
							height="4"
						/>
						<v-alert
							v-if="cleanerConnectionError"
							type="error"
							dense
							outlined
							prominent
							class="mb-0"
						>
							{{ cleanerConnectionError }}
						</v-alert>
						<v-data-table
							v-if="cleanerConnectionPayload"
							:headers="cleanerConnectionHeaders"
							:items="cleanerConnectionPayload.rows"
							:loading="cleanerConnectionLoading"
							:items-per-page="25"
							:footer-props="footerProps"
							class="cleaning-work-plan__data-table cleaning-work-plan__data-table--cleaners"
							:header-props="{ sortIcon: icon.sort }"
							mobile-breakpoint="0"
							dense
						>
							<template #item.floors="{ item }">
								<span class="cleaning-work-plan__floors-cell">{{ cleanerFloorsDisplay(item) }}</span>
							</template>
							<template #item.floorCount="{ item }">
								<span class="cleaning-work-plan__num">{{ formatNum(item.floorCount) }}</span>
							</template>
							<template #item.roomCount="{ item }">
								<span class="cleaning-work-plan__num">{{ formatNum(item.roomCount) }}</span>
							</template>
							<template #item.totalVisits="{ item }">
								<span class="cleaning-work-plan__num">{{ formatNum(item.totalVisits) }}</span>
							</template>
							<template #item.totalArrivals="{ item }">
								<span class="cleaning-work-plan__num">{{ formatNum(item.totalArrivals) }}</span>
							</template>
							<template #item.visitArrivalRatio="{ item }">
								<span class="cleaning-work-plan__num">{{ formatVisitArrivalRatio(item) }}</span>
							</template>
						</v-data-table>
					</v-expansion-panel-content>
				</v-expansion-panel>
			</v-expansion-panels>
		</v-card>

		<!-- 5. Rooms (collapsed by default) -->
		<template v-if="result && result.rooms && result.rooms.length">
			<v-card
				outlined
				class="cleaning-work-plan__card cleaning-work-plan__table-card cleaning-work-plan__card--collapsible pa-0"
			>
				<v-expansion-panels v-model="roomsTableExpanded" flat multiple tile>
					<v-expansion-panel>
						<v-expansion-panel-header class="subtitle-1 font-weight-medium">
							{{ $t('cleaning_work_plan_rooms_table') }}
						</v-expansion-panel-header>
						<v-expansion-panel-content class="pt-0 px-4 pb-4">
							<div class="cleaning-work-plan__table-head-inner cleaning-work-plan__rooms-panel-search-row">
								<v-text-field
									v-model="roomSearch"
									dense
									clearable
									hide-details
									single-line
									outlined
									:prepend-inner-icon="icon.magnify"
									:label="$t('cleaning_work_plan_search_rooms')"
									class="cleaning-work-plan__search"
								/>
							</div>
							<v-data-table
								:headers="headers"
								:items="filteredRooms"
								:items-per-page="25"
								:footer-props="footerProps"
								class="cleaning-work-plan__data-table cleaning-work-plan__data-table--rooms"
								:header-props="{ sortIcon: icon.sort }"
								mobile-breakpoint="0"
								dense
							>
								<!-- Custom tbody: one <td> per header so columns always match thead (avoids Row/slot off-by-one). -->
								<template #body="{ items, headers: hList }">
									<tbody>
										<tr
											v-for="(item, index) in items"
											:key="item.roomId != null ? item.roomId : index"
											class="cleaning-work-plan__row"
										>
											<td
												v-for="h in hList"
												:key="h.value"
												:style="headerCellWidthStyle(h)"
												:class="[
													`text-${h.align || 'start'}`,
													h.cellClass,
													{ 'v-data-table__divider': h.divider },
												]"
											>
												<template v-if="h.value === 'demandVisits'">
													<span class="cleaning-work-plan__num">{{ formatNum(item.demandVisits) }}</span>
												</template>
												<template v-else-if="h.value === 'arrivals'">
													<span class="cleaning-work-plan__num">{{ formatNum(item.arrivals) }}</span>
												</template>
												<template v-else-if="h.value === 'impliedCleansInWindow'">
													<span class="cleaning-work-plan__num cleaning-work-plan__num--emphasis">{{
														formatNum(item.impliedCleansInWindow)
													}}</span>
												</template>
												<template v-else>
													{{ roomTableTextCell(item, h.value) }}
												</template>
											</td>
										</tr>
										<tr v-if="!items.length" class="v-data-table__empty-wrapper">
											<td :colspan="hList.length">{{ $t('no_results_found') }}</td>
										</tr>
									</tbody>
								</template>
							</v-data-table>
						</v-expansion-panel-content>
					</v-expansion-panel>
				</v-expansion-panels>
			</v-card>
		</template>
	</v-container>
</template>

<script>
import {
	mdiChevronDown,
	mdiChevronLeft,
	mdiChevronRight,
	mdiDownload,
	mdiMagnify,
	mdiPageFirst,
	mdiPageLast,
	mdiRefresh,
	mdiSync,
} from '@mdi/js';
import { mapGetters } from 'vuex';
import { saveAs } from 'file-saver';
import { computeCleaningWorkPlan, fetchCleanerConnectionStats, requestAiAllocation } from '../services/workPlanService';

const workPlanIcons = {
	refresh: mdiRefresh,
	download: mdiDownload,
	magnify: mdiMagnify,
	sort: mdiChevronDown,
	sync: mdiSync,
};

export default {
	name: 'CleaningWorkPlan',
	data() {
		return {
			icon: workPlanIcons,
			loading: false,
			result: null,
			errorMessage: '',
			defaultVisitsPerClean: 50,
			desiredEmployeeCount: '',
			costPerHour: '',
			avgMinutesPerClean: '',
			roomSearch: '',
			cleanerConnectionLoading: false,
			cleanerConnectionError: '',
			cleanerConnectionPayload: null,
			aiAllocationLoading: false,
			aiAllocationResult: null,
			aiAllocationError: '',
			aiAllocationElapsedSeconds: 0,
			_aiAllocationElapsedTimer: null,
			/** v-expansion-panels `multiple` — empty array = all panels collapsed */
			cleanersByRoomsExpanded: [],
			roomsTableExpanded: [],
		};
	},
	computed: {
		...mapGetters(['filterSelected', 'filterOptions', 'currentFilteringSite', 'isRTL']),
		footerProps() {
			return {
				itemsPerPageOptions: [10, 25, 50, 100, -1],
				prevIcon: this.isRTL ? mdiChevronRight : mdiChevronLeft,
				nextIcon: this.isRTL ? mdiChevronLeft : mdiChevronRight,
				firstIcon: this.isRTL ? mdiPageLast : mdiPageFirst,
				lastIcon: this.isRTL ? mdiPageFirst : mdiPageLast,
			};
		},
		canRun() {
			const f = this.filterSelected;
			if (!f || f.site == null || f.site === '') {
				return false;
			}
			const t = f.time;
			return !!(t && t.timeFrom && t.timeTo);
		},
		periodLabel() {
			const f = this.filterSelected;
			if (!f || !f.time) {
				return '—';
			}
			const { timeFrom, timeTo } = f.time;
			if (!timeFrom || !timeTo) {
				return '—';
			}
			if (timeFrom === timeTo) {
				return timeFrom;
			}
			return `${timeFrom} → ${timeTo}`;
		},
		summarySiteName() {
			const s =
				this.currentFilteringSite && this.currentFilteringSite.name != null
					? String(this.currentFilteringSite.name).trim()
					: '';
			return s || '—';
		},
		/** employees × working hours per employee (shift window sum); same basis as total cleaning hours in performance table. */
		workforceTotalCapacityHours() {
			const t = this.result && this.result.totals;
			if (!t) {
				return null;
			}
			const emp = Number(t.cleanersWorkingInSite) || 0;
			const perEmp = t.shiftWorkingHoursTotal;
			if (emp <= 0 || perEmp == null || !Number.isFinite(Number(perEmp))) {
				return null;
			}
			return emp * Number(perEmp);
		},
		parsedAvgMinutesForApi() {
			const s = String(this.avgMinutesPerClean ?? '').trim();
			if (s === '') {
				return null;
			}
			const n = Number(s);
			if (!Number.isFinite(n) || n <= 0) {
				return null;
			}
			return n;
		},
		/**
		 * Mean `clean_time` (minutes) from shift settings for shifts selected in the dashboard filter.
		 * Used only to prefill “Average cleaning time” when that field is still empty.
		 */
		defaultAvgMinutesFromShiftSettings() {
			const ids = this.normalizedShiftIds();
			if (!ids || !ids.length) {
				return null;
			}
			const opts = this.filterOptions && Array.isArray(this.filterOptions.shift) ? this.filterOptions.shift : [];
			const values = [];
			for (const id of ids) {
				const row = opts.find((o) => o != null && Number(o.id) === Number(id));
				if (!row) {
					continue;
				}
				const ct = row.clean_time;
				if (ct == null || ct === '') {
					continue;
				}
				const n = Number(ct);
				if (Number.isFinite(n) && n > 0) {
					values.push(n);
				}
			}
			if (!values.length) {
				return null;
			}
			const mean = values.reduce((a, b) => a + b, 0) / values.length;
			return Math.round(mean * 100) / 100;
		},
		canRunAi() {
			if (!this.result || !this.result.rooms || !this.result.rooms.length) {
				return false;
			}
			const emp = Number(this.desiredEmployeeCount);
			if (!Number.isFinite(emp) || emp < 1) {
				return false;
			}
			const vpc = Number(this.defaultVisitsPerClean);
			if (!Number.isFinite(vpc) || vpc <= 0) {
				return false;
			}
			if (this.parsedAvgMinutesForApi == null) {
				return false;
			}
			return true;
		},
		/** Time-based hint matching the server’s multi-step AI chain (approximate). */
		aiAllocationStatusStepIndex() {
			if (!this.aiAllocationLoading) {
				return 0;
			}
			return Math.min(4, Math.floor(this.aiAllocationElapsedSeconds / 9));
		},
		aiAllocationStatusMessage() {
			const keys = [
				'cleaning_work_plan_ai_status_step_1',
				'cleaning_work_plan_ai_status_step_2',
				'cleaning_work_plan_ai_status_step_3',
				'cleaning_work_plan_ai_status_step_4',
				'cleaning_work_plan_ai_status_step_5',
			];
			return this.$t(keys[this.aiAllocationStatusStepIndex]);
		},
		filteredRooms() {
			const rooms = (this.result && this.result.rooms) || [];
			const q = (this.roomSearch || '').trim().toLowerCase();
			if (!q) {
				return rooms;
			}
			return rooms.filter((r) => {
				const hay = [
					r.roomName,
					r.building,
					r.floor,
					r.zone,
					r.gender,
					this.$roomTypeLabel(r.gender),
					String(r.roomId),
				]
					.filter(Boolean)
					.join(' ')
					.toLowerCase();
				return hay.includes(q);
			});
		},
		headers() {
			const col = (text, value, extra = {}) => {
				return {
					text,
					value,
					class: `cwp-col cwp-col--${value}`,
					cellClass: `cwp-col cwp-col--${value}`,
					...extra,
				};
			};
			const h = [
				col(this.$t('roomName'), 'roomName', { align: 'start', sortable: true }),
				col(this.$t('building'), 'building', { align: 'start', sortable: true }),
				col(this.$t('floor'), 'floor', { align: 'start', sortable: true }),
				col(this.$t('gender'), 'gender', { align: 'start', sortable: true }),
				col(this.$t('visits'), 'demandVisits', { align: 'start', sortable: true }),
				col(this.$t('cleaner_arrivals'), 'arrivals', { align: 'start', sortable: true }),
				col(this.$t('cleaning_work_plan_suggested_cleans'), 'impliedCleansInWindow', { align: 'start', sortable: true }),
			];
			return this.assignEqualColumnWidths(h);
		},
		cleanerConnectionHeaders() {
			const col = (text, value, extra = {}) => ({
				text,
				value,
				class: `cwp-cleaner-col cwp-cleaner-col--${value}`,
				cellClass: `cwp-cleaner-col cwp-cleaner-col--${value}`,
				...extra,
			});
			const h = [
				col(this.$t('username'), 'username', { align: 'start', sortable: true }),
				col(this.$t('cleaning_work_plan_connected_floors_list'), 'floors', { align: 'start', sortable: false }),
				col(this.$t('cleaning_work_plan_connected_floor_count'), 'floorCount', { align: 'start', sortable: true }),
				col(this.$t('cleaning_work_plan_connected_rooms_count'), 'roomCount', { align: 'start', sortable: true }),
				col(this.$t('visits'), 'totalVisits', { align: 'start', sortable: true }),
				col(this.$t('cleaner_arrivals'), 'totalArrivals', { align: 'start', sortable: true }),
				col(this.$t('cleaning_work_plan_visitor_cleaning_entry_ratio'), 'visitArrivalRatio', {
					align: 'start',
					sortable: false,
				}),
			];
			return this.assignEqualColumnWidths(h);
		},
		aiAllocationHeaders() {
			const col = (text, value, extra = {}) => ({
				text,
				value,
				class: `cwp-cleaner-col cwp-cleaner-col--${value}`,
				cellClass: `cwp-cleaner-col cwp-cleaner-col--${value}`,
				...extra,
			});
			const h = [
				col(this.$t('cleaning_work_plan_ai_cleaner_number'), 'cleanerNumber', { align: 'start', sortable: true }),
				col(this.$t('cleaning_work_plan_connected_floors_list'), 'floorsAssigned', { align: 'start', sortable: false }),
				col(this.$t('cleaning_work_plan_connected_floor_count'), 'numberOfFloors', { align: 'start', sortable: true }),
				col(this.$t('cleaning_work_plan_connected_rooms_count'), 'numberOfRooms', { align: 'start', sortable: true }),
				col(this.$t('visits'), 'visits', { align: 'start', sortable: true }),
				col(this.$t('cleaner_arrivals'), 'arrivals', { align: 'start', sortable: true }),
				col(this.$t('cleaning_work_plan_visitor_cleaning_entry_ratio'), 'ratio', {
					align: 'start',
					sortable: true,
				}),
			];
			return this.assignEqualColumnWidths(h);
		},
		/**
		 * Visit metrics first; then number of employees (directly before working hours per employee), then hours + daily cost.
		 */
		performanceComparisonRows() {
			const fmt = (n) => this.formatNum(n);
			const fmtH = (h) => this.formatShiftWorkingHours(h);
			const emptyRec = '—';
			const aiRec = this.buildAiRecommendedPerformanceMap();
			const pickRec = (key) => (aiRec && aiRec[key] != null ? aiRec[key] : emptyRec);

			const t = this.result && this.result.totals;
			const workforceHead = [];
			const workforceTail = [];
			const parsedCostPerHour = Number(this.costPerHour);
			const hasCostPerHour = Number.isFinite(parsedCostPerHour) && parsedCostPerHour > 0;
			const fmtDailyCost = (totalWorkingHours) => {
				if (!hasCostPerHour || totalWorkingHours == null || !Number.isFinite(Number(totalWorkingHours))) {
					return emptyRec;
				}
				const v = Number(totalWorkingHours) * parsedCostPerHour;
				return v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
			};
			if (t) {
				workforceHead.push({
					key: 'perf_num_employees',
					indexKey: 'cleaning_work_plan_perf_number_of_employees',
					existing: fmt(t.cleanersWorkingInSite),
					recommended: pickRec('perf_num_employees'),
				});
				workforceTail.push(
					{
						key: 'perf_hours_per_employee',
						indexKey: 'cleaning_work_plan_perf_working_hours_per_employee',
						existing: fmtH(t.shiftWorkingHoursTotal),
						recommended: pickRec('perf_hours_per_employee'),
					},
					{
						key: 'perf_total_cleaning_hours',
						indexKey: 'cleaning_work_plan_perf_total_cleaning_hours',
						existing: fmtH(this.workforceTotalCapacityHours),
						recommended: pickRec('perf_total_cleaning_hours'),
					},
					{
						key: 'perf_total_working_hours',
						indexKey: 'cleaning_work_plan_perf_total_working_hours',
						existing: fmtH(this.workforceTotalCapacityHours),
						recommended: pickRec('perf_total_working_hours'),
					},
					{
						key: 'perf_daily_cost',
						indexKey: 'cleaning_work_plan_perf_daily_cost',
						existing: fmtDailyCost(this.workforceTotalCapacityHours),
						recommended: pickRec('perf_daily_cost'),
					},
				);
			}

			const payload = this.cleanerConnectionPayload;
			const rows = payload && Array.isArray(payload.rows) ? payload.rows : [];
			if (!rows.length) {
				return [...workforceHead, ...workforceTail];
			}
			let workAreas = rows.filter((r) => (Number(r.roomCount) || 0) > 0);
			if (!workAreas.length) {
				workAreas = rows;
			}
			const totalCleaningVisits =
				t && t.totalArrivals != null
					? Number(t.totalArrivals)
					: workAreas.reduce((s, r) => s + (Number(r.totalArrivals) || 0), 0);
			let maxVisits = -Infinity;
			let minVisits = Infinity;
			for (const r of workAreas) {
				const v = Number(r.totalVisits) || 0;
				if (v > maxVisits) {
					maxVisits = v;
				}
				if (v < minVisits) {
					minVisits = v;
				}
			}
			const busiest = workAreas.filter((r) => (Number(r.totalVisits) || 0) === maxVisits);
			const leastBusy = workAreas.filter((r) => (Number(r.totalVisits) || 0) === minVisits);
			const cleaningToBusiest = busiest.reduce((s, r) => s + (Number(r.totalArrivals) || 0), 0);
			const cleaningToLeast = leastBusy.reduce((s, r) => s + (Number(r.totalArrivals) || 0), 0);
			const ratios = workAreas
				.map((r) => {
					const visits = Number(r.totalVisits) || 0;
					const arrivals = Number(r.totalArrivals) || 0;
					if (arrivals <= 0) {
						return null;
					}
					return visits / arrivals;
				})
				.filter((x) => x != null && Number.isFinite(x));
			let maxRatio = '—';
			let minRatio = '—';
			if (ratios.length) {
				const mx = Math.max(...ratios);
				const mn = Math.min(...ratios);
				maxRatio = fmt(Math.round(mx));
				minRatio = fmt(Math.round(mn));
			}
			const maxVisitsDisplay = maxVisits === -Infinity ? null : maxVisits;
			const minVisitsDisplay = minVisits === Infinity ? null : minVisits;
			const visitRows = [
				{
					key: 'total_cleaning',
					indexKey: 'cleaning_work_plan_perf_total_cleaning_visits',
					existing: fmt(totalCleaningVisits),
					recommended: pickRec('total_cleaning'),
				},
				{
					key: 'busiest_visits',
					indexKey: 'cleaning_work_plan_perf_busiest_area_visits',
					existing: fmt(maxVisitsDisplay),
					recommended: pickRec('busiest_visits'),
				},
				{
					key: 'busiest_cleaning',
					indexKey: 'cleaning_work_plan_perf_cleaning_to_busiest',
					existing: fmt(cleaningToBusiest),
					recommended: pickRec('busiest_cleaning'),
				},
				{
					key: 'max_ratio',
					indexKey: 'cleaning_work_plan_perf_max_cleaning_ratio',
					existing: maxRatio,
					recommended: pickRec('max_ratio'),
				},
				{
					key: 'least_visits',
					indexKey: 'cleaning_work_plan_perf_least_busy_visits',
					existing: fmt(minVisitsDisplay),
					recommended: pickRec('least_visits'),
				},
				{
					key: 'least_cleaning',
					indexKey: 'cleaning_work_plan_perf_cleaning_to_least',
					existing: fmt(cleaningToLeast),
					recommended: pickRec('least_cleaning'),
				},
				{
					key: 'min_ratio',
					indexKey: 'cleaning_work_plan_perf_min_cleaning_ratio',
					existing: minRatio,
					recommended: pickRec('min_ratio'),
				},
			];
			return [...visitRows, ...workforceHead, ...workforceTail];
		},
		peakDayLabel() {
			const d = this.result && this.result.meta && this.result.meta.peakDay;
			if (d == null || String(d).trim() === '') {
				return '—';
			}
			return String(d);
		},
		selectedBuildings() {
			const building = this.filterSelected && this.filterSelected.building;
			if (!building) return [];
			return Array.isArray(building) ? building.filter(Boolean) : [building];
		},
		buildingOptions() {
			const map = this.filterOptions && this.filterOptions.building;
			if (!map || typeof map !== 'object' || Array.isArray(map)) return [];
			return Object.keys(map)
				.filter((name) => name && String(name).trim() !== '')
				.sort((a, b) => a.localeCompare(b));
		},
		summaryBuildingsLabel() {
			const names = this.result && this.result.meta && this.result.meta.buildings;
			return Array.isArray(names) ? names.join(', ') : '';
		},
		/** Human-readable shift names for summary, matching meta.shiftIds order. */
		summaryShiftNamesLabel() {
			const m = this.result && this.result.meta;
			const ids = m && Array.isArray(m.shiftIds) ? m.shiftIds : [];
			if (!ids.length) {
				return '';
			}

			const opts = this.filterOptions && Array.isArray(this.filterOptions.shift) ? this.filterOptions.shift : [];
			/** @type {Map<number, string>} */
			const byId = new Map();
			for (const o of opts) {
				if (o == null || o.id == null) {
					continue;
				}
				const id = Number(o.id);
				if (Number.isNaN(id)) {
					continue;
				}
				const name = o.name != null ? String(o.name).trim() : '';
				if (name) {
					byId.set(id, name);
				}
			}

			/** @type {Map<number, string>} */
			const fromSelection = new Map();
			const selected = this.filterSelected && this.filterSelected.shift;
			if (Array.isArray(selected)) {
				for (const x of selected) {
					if (x != null && typeof x === 'object') {
						const id = Number(x.value != null ? x.value : x.id);
						if (Number.isNaN(id)) {
							continue;
						}
						const nm = x.name || x.text || x.label;
						if (nm != null && String(nm).trim() !== '') {
							fromSelection.set(id, String(nm).trim());
						}
					}
				}
			}

			return ids
				.map((rawId) => {
					const id = Number(rawId);
					if (!Number.isNaN(id) && fromSelection.has(id)) {
						return fromSelection.get(id);
					}
					if (!Number.isNaN(id) && byId.has(id)) {
						return byId.get(id);
					}
					return String(rawId);
				})
				.join(', ');
		},
	},
	watch: {
		'$route.name'() {
			this.roomSearch = '';
		},
		filterSelected: {
			deep: true,
			handler() {
				this.loadCleanerConnectionStats();
			},
			immediate: true,
		},
		defaultAvgMinutesFromShiftSettings: {
			handler(val) {
				if (val == null) {
					return;
				}
				const cur = String(this.avgMinutesPerClean ?? '').trim();
				if (cur !== '') {
					return;
				}
				this.avgMinutesPerClean = String(val);
			},
			immediate: true,
		},
	},
	methods: {
		formatNum(n) {
			if (n == null || Number.isNaN(Number(n))) {
				return '—';
			}
			return Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
		},
		/** Total scheduled shift window length (hours), formatted as a number only — no unit suffix in cells. */
		formatShiftWorkingHours(h) {
			if (h == null || !Number.isFinite(Number(h))) {
				return '—';
			}
			return Number(h).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
		},
		/**
		 * Match Vuetify header desktop: width + minWidth on th so tbody cells stay aligned under table-layout: fixed.
		 */
		headerCellWidthStyle(header) {
			const w = header && header.width;
			if (w == null || w === '') {
				return {};
			}
			const unit = typeof w === 'number' ? `${w}px` : String(w);
			return {
				width: unit,
				minWidth: unit,
				boxSizing: 'border-box',
			};
		},
		/** Identical width on every column (fixed table layout shares space evenly). */
		assignEqualColumnWidths(headers) {
			if (!headers || !headers.length) {
				return headers;
			}
			const n = headers.length;
			const w = n === 1 ? '100%' : `${(100 / n).toFixed(6)}%`;
			return headers.map((h) => ({ ...h, width: w }));
		},
		roomTableTextCell(item, key) {
			const v = item[key];
			if (v == null) {
				return '';
			}
			if (key === 'gender') {
				return this.$roomTypeLabel(v);
			}
			return String(v);
		},
		cleanerFloorsDisplay(item) {
			const list = item && Array.isArray(item.floors) ? item.floors : [];
			if (!list.length) {
				return '—';
			}
			return list.join(', ');
		},
		formatVisitArrivalRatio(item) {
			const arrivals = Number(item && item.totalArrivals);
			const visits = Number(item && item.totalVisits);
			if (!Number.isFinite(arrivals) || arrivals <= 0 || !Number.isFinite(visits)) {
				return '—';
			}
			const ratio = visits / arrivals;
			return this.formatNum(Math.round(ratio));
		},
		formatRatio(val) {
			const r = Number(val);
			if (!Number.isFinite(r)) {
				return '—';
			}
			return this.formatNum(Math.round(r));
		},
		/**
		 * Maps AI allocation rows to "Buzzztech recommended plan" cells (same row keys as performanceComparisonRows).
		 * Treats each AI cleaner row like a work-area row: busiest / least by visits; cleaning load = arrivals (rounded visits÷N).
		 * @returns {Record<string, string>|null}
		 */
		buildAiRecommendedPerformanceMap() {
			const allocs = this.aiAllocationResult && Array.isArray(this.aiAllocationResult.allocations)
				? this.aiAllocationResult.allocations
				: null;
			if (!allocs || !allocs.length) {
				return null;
			}
			const t = this.result && this.result.totals;
			const N = Number(this.desiredEmployeeCount);
			if (!t || !Number.isFinite(N) || N < 1) {
				return null;
			}

			const fmt = (n) => this.formatNum(n);
			const fmtH = (h) => this.formatShiftWorkingHours(h);
			const emptyRec = '—';

			let totalImplied = 0;
			let maxVis = -Infinity;
			let minVis = Infinity;
			for (const a of allocs) {
				totalImplied += Number(a.arrivals) || 0;
				const v = Number(a.visits) || 0;
				if (v > maxVis) {
					maxVis = v;
				}
				if (v < minVis) {
					minVis = v;
				}
			}
			const busiest = allocs.filter((a) => (Number(a.visits) || 0) === maxVis);
			const leastBusy = allocs.filter((a) => (Number(a.visits) || 0) === minVis);
			const cleaningToBusiest = busiest.reduce((s, a) => s + (Number(a.arrivals) || 0), 0);
			const cleaningToLeast = leastBusy.reduce((s, a) => s + (Number(a.arrivals) || 0), 0);

			const ratios = allocs
				.map((r) => {
					const visits = Number(r.visits) || 0;
					const arr = Number(r.arrivals) || 0;
					if (arr <= 0) {
						return null;
					}
					return visits / arr;
				})
				.filter((x) => x != null && Number.isFinite(x));
			let maxRatioStr = emptyRec;
			let minRatioStr = emptyRec;
			if (ratios.length) {
				maxRatioStr = fmt(Math.round(Math.max(...ratios)));
				minRatioStr = fmt(Math.round(Math.min(...ratios)));
			}

			const shiftH = t.shiftWorkingHoursTotal;
			const shiftOk = shiftH != null && Number.isFinite(Number(shiftH));
			const recCap = shiftOk ? N * Number(shiftH) : null;

			const parsedCostPerHour = Number(this.costPerHour);
			const hasCostPerHour = Number.isFinite(parsedCostPerHour) && parsedCostPerHour > 0;
			const fmtDailyCost = (totalWorkingHours) => {
				if (!hasCostPerHour || totalWorkingHours == null || !Number.isFinite(Number(totalWorkingHours))) {
					return emptyRec;
				}
				const v = Number(totalWorkingHours) * parsedCostPerHour;
				return v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
			};

			return {
				total_cleaning: fmt(totalImplied),
				busiest_visits: maxVis === -Infinity ? emptyRec : fmt(maxVis),
				busiest_cleaning: fmt(cleaningToBusiest),
				max_ratio: maxRatioStr,
				least_visits: minVis === Infinity ? emptyRec : fmt(minVis),
				least_cleaning: fmt(cleaningToLeast),
				min_ratio: minRatioStr,
				perf_num_employees: fmt(N),
				perf_hours_per_employee: shiftOk ? fmtH(shiftH) : emptyRec,
				perf_total_cleaning_hours: recCap != null ? fmtH(recCap) : emptyRec,
				perf_total_working_hours: recCap != null ? fmtH(recCap) : emptyRec,
				perf_daily_cost: fmtDailyCost(recCap),
			};
		},
		onBuildingsInput(names) {
			const value = Array.isArray(names) && names.length ? names : null;
			this.$store.commit('updateFilter', { field: 'building', value });
		},
		normalizedBuildings() {
			return this.selectedBuildings.length ? this.selectedBuildings : undefined;
		},
		normalizedShiftIds() {
			const s = this.filterSelected && this.filterSelected.shift;
			if (!s || !s.length) {
				return undefined;
			}
			const ids = s
				.map((x) => {
					if (x != null && typeof x === 'object') {
						return Number(x.value != null ? x.value : x.id);
					}
					return Number(x);
				})
				.filter((id) => !Number.isNaN(id));
			return ids.length ? ids : undefined;
		},
		async loadCleanerConnectionStats() {
			this.cleanerConnectionError = '';
			if (!this.canRun) {
				this.cleanerConnectionPayload = null;
				this.cleanerConnectionLoading = false;
				return;
			}
			this.cleanerConnectionLoading = true;
			try {
				const body = {
					siteId: Number(this.filterSelected.site),
					time: {
						timeFrom: this.filterSelected.time.timeFrom,
						timeTo: this.filterSelected.time.timeTo,
					},
					shiftIds: this.normalizedShiftIds(),
					buildings: this.normalizedBuildings(),
				};
				this.cleanerConnectionPayload = await fetchCleanerConnectionStats(body);
			} catch (e) {
				this.cleanerConnectionPayload = null;
				const d = e && e.response && e.response.data;
				const msg =
					typeof d === 'string'
						? d
						: d && d.message
							? d.message
							: e && e.message
								? e.message
								: this.$t('cleaning_work_plan_error');
				this.cleanerConnectionError = msg;
			} finally {
				this.cleanerConnectionLoading = false;
			}
		},
		async run() {
			this.errorMessage = '';
			this.result = null;
			this.roomSearch = '';
			this.roomsTableExpanded = [];
			this.aiAllocationResult = null;
			this.aiAllocationError = '';
			if (!this.canRun) {
				return;
			}
			const fromDate = new Date(this.filterSelected.time.timeFrom);
			const toDate = new Date(this.filterSelected.time.timeTo);
			if (fromDate > toDate) {
				this.errorMessage = this.$t('date_range_invalid');
				return;
			}
			this.loading = true;
			try {
				const body = {
					siteId: Number(this.filterSelected.site),
					time: {
						timeFrom: this.filterSelected.time.timeFrom,
						timeTo: this.filterSelected.time.timeTo,
					},
					defaultVisitsPerClean: this.defaultVisitsPerClean,
					shiftIds: this.normalizedShiftIds(),
					buildings: this.normalizedBuildings(),
				};
				const avgMin = this.parsedAvgMinutesForApi;
				if (avgMin != null) {
					body.avgMinutesPerClean = avgMin;
				}
				const data = await computeCleaningWorkPlan(body);
				this.result = data;
			} catch (e) {
				const d = e && e.response && e.response.data;
				const msg =
					typeof d === 'string'
						? d
						: d && d.message
							? d.message
							: e && e.message
								? e.message
								: this.$t('cleaning_work_plan_error');
				this.errorMessage = msg;
			} finally {
				this.loading = false;
			}
		},
		/**
		 * Exports an HTML table as .xls so Microsoft Excel applies left alignment and text formatting
		 * (plain CSV cannot control cell alignment when opened in Excel).
		 */
		exportCsv() {
			if (!this.result || !this.result.rooms || !this.result.rooms.length) {
				return;
			}
			const escHtml = (v) => {
				if (v == null) {
					return '';
				}
				return String(v)
					.replace(/&/g, '&amp;')
					.replace(/</g, '&lt;')
					.replace(/>/g, '&gt;')
					.replace(/"/g, '&quot;');
			};
			/* mso-number-format:\\@ forces "text" so numbers stay left-aligned like headers */
			const cs =
				'text-align:left;vertical-align:top;mso-number-format:\\@;border:1px solid #ccc;padding:4px 8px;';
			const csHead = `${cs}font-weight:bold;background:#f5f5f5;`;
			const csSection = 'text-align:left;vertical-align:top;padding:12px 4px 6px 4px;font-weight:bold;font-size:13px;border:none;';
			const trCells = (cells, header) => {
				const tag = header ? 'th' : 'td';
				const st = header ? csHead : cs;
				return `<tr>${cells.map((c) => `<${tag} style="${st}">${escHtml(c)}</${tag}>`).join('')}</tr>`;
			};
			const sectionRow = (title, colspan) =>
				`<tr><td colspan="${colspan}" style="${csSection}">${escHtml(title)}</td></tr>`;

			const siteName =
				this.currentFilteringSite && this.currentFilteringSite.name != null
					? String(this.currentFilteringSite.name).trim()
					: '';
			const parts = [];
			parts.push(
				'<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">',
			);
			parts.push('<head><meta charset="utf-8">');
			parts.push(
				'<style>table{border-collapse:collapse;font-family:Calibri,Arial,sans-serif;font-size:11pt;width:100%;}</style>',
			);
			parts.push('</head><body>');
			const t = this.result.totals || {};
			const openTable = () => '<table>';
			const closeTable = () => '</table>';
			const gap = '<br/>';

			/* --- 1. Summary --- */
			parts.push(openTable());
			parts.push(sectionRow(this.$t('cleaning_work_plan_summary'), 2));
			const summaryPairs = [
				[this.$t('site_name'), siteName],
				[this.$t('cleaning_work_plan_room_count'), this.formatNum(t.roomCount)],
				[this.$t('cleaning_work_plan_peak_day'), this.peakDayLabel],
				[this.$t('cleaning_work_plan_summary_filter_period'), this.periodLabel],
			];
			const shiftsCsv = this.summaryShiftNamesLabel;
			if (shiftsCsv) {
				summaryPairs.push([this.$t('shifts'), shiftsCsv]);
			}
			const buildingsCsv = this.summaryBuildingsLabel;
			if (buildingsCsv) {
				summaryPairs.push([this.$t('building'), buildingsCsv]);
			}
			summaryPairs.push(
				[this.$t('cleaning_work_plan_summary_total_visits'), this.formatNum(t.totalVisits)],
				[this.$t('cleaning_work_plan_summary_total_arrivals'), this.formatNum(t.totalArrivals)],
				[this.$t('cleaning_work_plan_summary_employees_in_site'), this.formatNum(t.cleanersWorkingInSite)],
				[
					this.$t('cleaning_work_plan_summary_shift_working_hours'),
					this.formatShiftWorkingHours(t.shiftWorkingHoursTotal),
				],
			);
			for (const [a, b] of summaryPairs) {
				parts.push(trCells([a, b], false));
			}
			parts.push(closeTable());

			/* --- 2. Performance Comparison --- */
			const perfRows = this.performanceComparisonRows;
			if (perfRows && perfRows.length) {
				parts.push(gap);
				parts.push(openTable());
				parts.push(sectionRow(this.$t('cleaning_work_plan_performance_comparison_table'), 3));
				parts.push(
					trCells(
						[
							this.$t('cleaning_work_plan_performance_col_index'),
							this.$t('cleaning_work_plan_performance_existing_plan'),
							this.$t('cleaning_work_plan_performance_recommended_plan'),
						],
						true,
					),
				);
				for (const row of perfRows) {
					parts.push(trCells([this.$t(row.indexKey), row.existing, row.recommended], false));
				}
				parts.push(closeTable());
			}

			/* --- 3. Rooms --- */
			const roomColMap = {
				roomName: 'roomName',
				building: 'building',
				floor: 'floor',
				gender: 'gender',
				demandVisits: 'visits',
				arrivals: 'cleaner_arrivals',
				impliedCleansInWindow: 'cleaning_work_plan_suggested_cleans',
			};
			const roomCols = Object.keys(roomColMap);
			parts.push(gap);
			parts.push(openTable());
			parts.push(sectionRow(this.$t('cleaning_work_plan_rooms_table'), roomCols.length));
			parts.push(trCells(roomCols.map((c) => this.$t(roomColMap[c])), true));
			for (const r of this.result.rooms) {
				parts.push(trCells(roomCols.map((c) => r[c]), false));
			}
			parts.push(closeTable());

			/* --- 4. AI Allocation --- */
			const allocs = this.aiAllocationResult && Array.isArray(this.aiAllocationResult.allocations)
				? this.aiAllocationResult.allocations
				: [];
			if (allocs.length) {
				const aiHeaders = [
					this.$t('cleaning_work_plan_ai_cleaner_number'),
					this.$t('cleaning_work_plan_connected_floors_list'),
					this.$t('cleaning_work_plan_connected_floor_count'),
					this.$t('cleaning_work_plan_connected_rooms_count'),
					this.$t('visits'),
					this.$t('cleaner_arrivals'),
					this.$t('cleaning_work_plan_visitor_cleaning_entry_ratio'),
				];
				parts.push(gap);
				parts.push(openTable());
				parts.push(sectionRow(this.$t('cleaning_work_plan_ai_allocation_result'), aiHeaders.length));
				parts.push(trCells(aiHeaders, true));
				for (const a of allocs) {
					parts.push(
						trCells(
							[
								a.cleanerNumber,
								a.floorsAssigned,
								a.numberOfFloors,
								a.numberOfRooms,
								a.visits,
								a.arrivals,
								this.formatRatio(a.ratio),
							],
							false,
						),
					);
				}
				parts.push(closeTable());
			}

			/* --- 5. Cleaners Connection --- */
			const ccRows = this.cleanerConnectionPayload && Array.isArray(this.cleanerConnectionPayload.rows)
				? this.cleanerConnectionPayload.rows
				: [];
			if (ccRows.length) {
				const ccHeaders = [
					this.$t('username'),
					this.$t('cleaning_work_plan_connected_floors_list'),
					this.$t('cleaning_work_plan_connected_floor_count'),
					this.$t('cleaning_work_plan_connected_rooms_count'),
					this.$t('visits'),
					this.$t('cleaner_arrivals'),
					this.$t('cleaning_work_plan_visitor_cleaning_entry_ratio'),
				];
				parts.push(gap);
				parts.push(openTable());
				parts.push(sectionRow(this.$t('cleaning_work_plan_cleaners_connection_table'), ccHeaders.length));
				parts.push(trCells(ccHeaders, true));
				for (const row of ccRows) {
					parts.push(
						trCells(
							[
								row.username,
								this.cleanerFloorsDisplay(row),
								row.floorCount,
								row.roomCount,
								row.totalVisits,
								row.totalArrivals,
								this.formatVisitArrivalRatio(row),
							],
							false,
						),
					);
				}
				parts.push(closeTable());
			}

			parts.push('</body></html>');
			const html = parts.join('');
			const blob = new Blob(['\uFEFF' + html], {
				type: 'application/vnd.ms-excel;charset=utf-8',
			});
			const fileBase = this.sanitizeCsvFilenameBase(siteName) || `site-${this.filterSelected.site}`;
			saveAs(blob, `cleaning-work-plan-${fileBase}.xls`);
		},
		sanitizeCsvFilenameBase(raw) {
			const s = String(raw ?? '').trim();
			if (!s) {
				return '';
			}
			return s
				.replace(/[/\\?%*:|"<>]/g, '-')
				.replace(/\s+/g, '-')
				.replace(/-+/g, '-')
				.replace(/^-|-$/g, '')
				.slice(0, 120);
		},
		async runAiAllocation() {
			if (!this.result || !this.result.rooms || !this.result.rooms.length) {
				return;
			}
			const emp = Number(this.desiredEmployeeCount);
			if (!Number.isFinite(emp) || emp < 1) {
				this.aiAllocationError = this.$t('cleaning_work_plan_ai_invalid_employees');
				return;
			}

			this.aiAllocationLoading = true;
			this.aiAllocationError = '';
			this.aiAllocationResult = null;
			this.startAiAllocationElapsedTimer();

			try {
				const rooms = this.result.rooms.map((r) => ({
					roomName: r.roomName != null ? String(r.roomName) : '',
					building: r.building != null ? String(r.building) : '',
					floor: r.floor != null ? String(r.floor) : '',
					zone: r.zone != null ? String(r.zone) : '',
					gender: r.gender != null ? String(r.gender) : '',
					demandVisits: Number(r.demandVisits) || 0,
					arrivals: Number(r.arrivals) || 0,
				}));

				const t = this.result.totals || {};
				const totalCleaningMinutes = Number(t.totalSuggestedMinutes) || null;
				const totalCleaningHours = Number(t.totalSuggestedHours) || null;

				const body = {
					rooms,
					desiredEmployees: emp,
					visitsPerClean: Number(this.defaultVisitsPerClean),
					avgMinutesPerClean: this.parsedAvgMinutesForApi,
					totalCleaningMinutes,
					totalCleaningHours,
				};
				const data = await requestAiAllocation(body);
				this.aiAllocationResult = data;
			} catch (e) {
				console.error('[AI Allocation]', e?.response?.data || e?.message || e);
				this.aiAllocationError = this.$t('cleaning_work_plan_ai_allocation_error');
			} finally {
				this.clearAiAllocationElapsedTimer();
				this.aiAllocationLoading = false;
			}
		},
		startAiAllocationElapsedTimer() {
			this.clearAiAllocationElapsedTimer();
			this.aiAllocationElapsedSeconds = 0;
			this._aiAllocationElapsedTimer = setInterval(() => {
				this.aiAllocationElapsedSeconds += 1;
			}, 1000);
		},
		clearAiAllocationElapsedTimer() {
			if (this._aiAllocationElapsedTimer) {
				clearInterval(this._aiAllocationElapsedTimer);
				this._aiAllocationElapsedTimer = null;
			}
		},
	},
	beforeDestroy() {
		this.clearAiAllocationElapsedTimer();
	},
};
</script>

<style scoped lang="scss">
@use "@/styles/vars.scss" as *;
@use "sass:color";

.cleaning-work-plan {
	max-width: 1280px;
	margin-inline: auto;
}

.cleaning-work-plan__card {
	border-radius: 8px !important;
	margin-bottom: 40px !important;
}

.cleaning-work-plan__card--collapsible {
	overflow: visible;
}

.cleaning-work-plan__card--collapsible .v-expansion-panel::before {
	box-shadow: none;
}

.cleaning-work-plan__card--collapsible ::v-deep .v-expansion-panel-content__wrap {
	overflow: visible;
}

.cleaning-work-plan__card--collapsible ::v-deep .v-data-table__wrapper {
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;
}

.cleaning-work-plan__rooms-panel-search-row {
	display: flex;
	flex-wrap: wrap;
	width: 100%;
	margin-bottom: 12px;
}

.cleaning-work-plan__rooms-panel-search-row .cleaning-work-plan__search {
	flex: 1 1 260px;
	margin-inline-start: 0;
	max-width: 360px;
}

@media (max-width: 960px) {
	.cleaning-work-plan__rooms-panel-search-row .cleaning-work-plan__search {
		max-width: none;
		flex: 1 1 100%;
	}
}

.cleaning-work-plan > .v-progress-linear,
.cleaning-work-plan > .v-alert {
	margin-bottom: 32px !important;
}

/* AI Allocate — distinct from primary “Generate” (portal blue) */
.cleaning-work-plan__btn-ai.v-btn {
	background-color: $wedgewood-blue !important;
	color: $white !important;
	border-color: transparent !important;
	box-shadow: 0 2px 8px rgba($wedgewood-blue, 0.35) !important;
	font-weight: $font-weight-semi-bold;
	letter-spacing: 0.02em;
	text-transform: none;
}

.cleaning-work-plan__btn-ai.v-btn:not(.v-btn--disabled):hover {
	background-color: color.adjust($wedgewood-blue, $lightness: -8%) !important;
	box-shadow: 0 3px 10px rgba($wedgewood-blue, 0.42) !important;
}

.cleaning-work-plan__btn-ai.v-btn.v-btn--disabled {
	background-color: rgba($wedgewood-blue, 0.42) !important;
	color: rgba($white, 0.92) !important;
	box-shadow: none !important;
}

.cleaning-work-plan__btn-ai ::v-deep .v-btn__content {
	color: $white !important;
}

.cleaning-work-plan--dark .cleaning-work-plan__btn-ai.v-btn:not(.v-btn--disabled) {
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35) !important;
}

/* Restore flex inside card actions (global .flex { flex: none } breaks Vuetify layouts) */
.cleaning-work-plan__actions.v-card__actions {
	display: flex !important;
	flex-wrap: wrap !important;
	align-items: center;
	gap: 8px 12px;
	width: 100%;
	box-sizing: border-box;
	background: rgba(0, 0, 0, 0.02);
	border-radius: 0 0 8px 8px;
}

.cleaning-work-plan--dark .cleaning-work-plan__actions.v-card__actions {
	background: rgba(255, 255, 255, 0.06);
}

.cleaning-work-plan__action-btn {
	flex: 0 1 auto;
	min-width: 0;
	white-space: nowrap;
}

.cleaning-work-plan__action-btn ::v-deep .v-btn__content {
	white-space: nowrap;
}

/* Generate plan — portal blue, white label (stronger than default Vuetify primary here) */
.cleaning-work-plan__btn-generate.v-btn {
	background-color: $blue !important;
	color: $white !important;
	border-color: transparent !important;
	box-shadow: 0 2px 8px rgba($black-blue, 0.22) !important;
	font-weight: $font-weight-semi-bold;
	letter-spacing: 0.02em;
	text-transform: none;
}

.cleaning-work-plan__btn-generate.v-btn:not(.v-btn--disabled):hover {
	background-color: color.adjust($blue, $lightness: -7%) !important;
	box-shadow: 0 3px 10px rgba($black-blue, 0.28) !important;
}

.cleaning-work-plan__btn-generate.v-btn.v-btn--disabled {
	background-color: rgba($blue, 0.38) !important;
	color: rgba($white, 0.92) !important;
	box-shadow: none !important;
}

.cleaning-work-plan__btn-generate ::v-deep .v-btn__content {
	color: $white !important;
}

.cleaning-work-plan__btn-generate-icon,
.cleaning-work-plan__btn-generate ::v-deep .v-icon {
	color: $white !important;
}

.cleaning-work-plan__btn-generate ::v-deep .v-progress-circular__overlay,
.cleaning-work-plan__btn-generate ::v-deep .v-progress-circular .v-icon {
	color: $white !important;
	stroke: $white !important;
}

@media (max-width: 768px) {
	.cleaning-work-plan__actions.v-card__actions {
		flex-direction: column;
		align-items: stretch;
	}

	.cleaning-work-plan__action-btn {
		width: 100% !important;
		flex: 1 1 auto !important;
	}
}

.cleaning-work-plan__summary-card .v-card__text {
	padding-top: 8px;
}

/* Section titles: direction + flex-start so first text edge matches reading order (LTR left, RTL right). */
.cleaning-work-plan__card > .v-card__title {
	direction: ltr;
	justify-content: flex-start !important;
	text-align: left;
	width: 100%;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__card > .v-card__title {
	direction: rtl;
	justify-content: flex-start !important;
	text-align: right;
}

/* Collapsible panels: titles use expansion headers (not direct .v-card__title children) */
.cleaning-work-plan__card--collapsible ::v-deep .v-expansion-panel-header {
	direction: ltr;
	justify-content: flex-start !important;
	text-align: left;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__card--collapsible ::v-deep .v-expansion-panel-header {
	direction: rtl;
	justify-content: flex-start !important;
	text-align: right;
}

.cleaning-work-plan__performance-table ::v-deep table {
	direction: ltr;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__performance-table ::v-deep table {
	direction: rtl;
}

.cleaning-work-plan__performance-table ::v-deep thead th,
.cleaning-work-plan__performance-table ::v-deep tbody td {
	direction: ltr;
	text-align: left !important;
}

/* Hebrew/RTL locales: first header item appears at the right, with rtl text inside cells */
.cleaning-work-plan--locale-rtl .cleaning-work-plan__performance-table ::v-deep thead th,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__performance-table ::v-deep tbody td {
	direction: rtl;
	text-align: right !important;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__performance-table ::v-deep td .cleaning-work-plan__num {
	direction: ltr;
	unicode-bidi: isolate;
}

.cleaning-work-plan__summary-note {
	max-width: 52rem;
	line-height: 1.5;
}

.cleaning-work-plan__stat {
	padding: 12px 14px;
	border-radius: 6px;
	background: rgba(0, 0, 0, 0.04);
	height: 100%;
}

.cleaning-work-plan--dark .cleaning-work-plan__stat {
	background: rgba(255, 255, 255, 0.08);
}

.cleaning-work-plan__stat-label {
	display: block;
	font-size: 11px;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	opacity: 0.7;
	margin-bottom: 4px;
}

.cleaning-work-plan__stat-value {
	font-size: 1.125rem;
	font-weight: 600;
	line-height: 1.3;
}

/* Match data-table numeric columns: same right alignment for summary counts */
.cleaning-work-plan__stat--numeric .cleaning-work-plan__stat-value {
	text-align: end;
	font-variant-numeric: tabular-nums;
}

.cleaning-work-plan__stat-value--wrap {
	white-space: normal;
	word-break: break-word;
	overflow-wrap: break-word;
}

.cleaning-work-plan__setting-field ::v-deep .v-label,
.cleaning-work-plan__setting-field ::v-deep input,
.cleaning-work-plan__search ::v-deep .v-label,
.cleaning-work-plan__search ::v-deep input {
	direction: ltr;
	text-align: left;
}

.cleaning-work-plan__setting-field ::v-deep .v-input__slot,
.cleaning-work-plan__setting-field ::v-deep .v-text-field__slot,
.cleaning-work-plan__search ::v-deep .v-input__slot,
.cleaning-work-plan__search ::v-deep .v-text-field__slot {
	direction: ltr;
}

.cleaning-work-plan__setting-field ::v-deep .v-label,
.cleaning-work-plan__search ::v-deep .v-label {
	left: 0 !important;
	right: auto !important;
	transform-origin: top left;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__setting-field ::v-deep .v-label,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__setting-field ::v-deep input,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__search ::v-deep .v-label,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__search ::v-deep input {
	direction: rtl;
	text-align: right;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__setting-field ::v-deep .v-input__slot,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__setting-field ::v-deep .v-text-field__slot,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__search ::v-deep .v-input__slot,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__search ::v-deep .v-text-field__slot {
	direction: rtl;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__setting-field ::v-deep .v-label,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__search ::v-deep .v-label {
	left: auto !important;
	right: 0 !important;
	transform-origin: top right;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table ::v-deep .v-data-footer {
	direction: rtl;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table ::v-deep .v-data-footer__pagination,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table ::v-deep .v-data-footer__select {
	direction: rtl;
}

.cleaning-work-plan__table-head {
	display: block !important;
	width: 100%;
	box-sizing: border-box;
	padding-bottom: 8px !important;
}

.cleaning-work-plan__table-head-inner {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: 12px;
	width: 100%;
	min-width: 0;
}

.cleaning-work-plan__table-heading {
	flex: 0 1 auto;
	min-width: 0;
	line-height: 1.4;
	padding-top: 8px;
	text-align: start;
}

.cleaning-work-plan__search {
	flex: 1 1 260px;
	width: 100%;
	max-width: 360px;
	min-width: 0;
	margin-inline-start: auto;
}

.cleaning-work-plan__search ::v-deep .v-input {
	min-width: 0;
}

@media (max-width: 960px) {
	.cleaning-work-plan__search {
		margin-inline-start: 0;
		max-width: none;
		flex: 1 1 100%;
	}
}

.cleaning-work-plan__data-table {
	border-radius: 0 0 8px 8px;
	width: 100%;
}

.cleaning-work-plan__data-table ::v-deep .v-data-table__wrapper {
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;
}

/*
 * Keep Vuetify’s normal table model: collapsed borders + width 100% so thead and tbody share one column grid.
 * The table direction follows locale: headers[0] is leftmost in LTR and rightmost in RTL.
 */
.cleaning-work-plan__data-table ::v-deep .v-data-table__wrapper > table {
	direction: ltr;
	table-layout: fixed;
	width: 100%;
	border-collapse: collapse;
	border-spacing: 0;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table ::v-deep .v-data-table__wrapper > table {
	direction: rtl;
}

/* vendor.scss sets .flex { flex: none } which breaks Vuetify header/cell flex alignment */
.cleaning-work-plan__data-table ::v-deep thead th .flex,
.cleaning-work-plan__data-table ::v-deep tbody td .flex {
	flex: 1 1 auto !important;
	min-width: 0;
}

.cleaning-work-plan__data-table ::v-deep thead th,
.cleaning-work-plan__data-table ::v-deep tbody td {
	box-sizing: border-box;
}

.cleaning-work-plan__data-table--rooms ::v-deep .v-data-table__wrapper > table {
	min-width: 1180px;
}

.cleaning-work-plan__data-table ::v-deep thead th,
.cleaning-work-plan__data-table ::v-deep tbody td {
	vertical-align: middle;
	padding: 0 12px !important;
	overflow: hidden;
	text-overflow: ellipsis;
}

/*
 * Explicit cell alignment: table column order stays LTR, cell content direction follows locale.
 */
.cleaning-work-plan__data-table ::v-deep thead > tr > th {
	direction: ltr;
	text-align: left !important;
}

.cleaning-work-plan__data-table ::v-deep thead > tr > th .v-data-table-header__content {
	justify-content: flex-start !important;
}

.cleaning-work-plan__data-table ::v-deep tbody > tr > td {
	direction: ltr;
	text-align: left !important;
}

.cleaning-work-plan__data-table ::v-deep tbody > tr > td .cleaning-work-plan__num {
	display: block;
	width: 100%;
	text-align: inherit;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table ::v-deep thead > tr > th,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table ::v-deep tbody > tr > td {
	direction: rtl;
	text-align: right !important;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table ::v-deep thead > tr > th .v-data-table-header__content {
	justify-content: flex-start !important;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table ::v-deep thead th .cleaning-work-plan__num,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table ::v-deep tbody td .cleaning-work-plan__num {
	direction: ltr;
	unicode-bidi: isolate;
}

/*
 * Same alignment for thead and tbody via column class (avoids header/body drift).
 * Rooms: text cols start, metrics end — mirrors cleaner table (floors start, metrics end).
 */
.cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--roomName,
.cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--roomName,
.cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--building,
.cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--building,
.cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--floor,
.cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--floor,
.cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--gender,
.cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--gender {
	text-align: left !important;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--roomName,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--roomName,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--building,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--building,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--floor,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--floor,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--gender,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--gender {
	text-align: right !important;
}

/* Rooms table: metric headers and values follow the active locale direction */
.cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--demandVisits,
.cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--arrivals,
.cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--impliedCleansInWindow {
	text-align: left !important;
}

.cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--demandVisits .v-data-table-header__content,
.cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--arrivals .v-data-table-header__content,
.cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--impliedCleansInWindow .v-data-table-header__content {
	justify-content: flex-start !important;
}

.cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--demandVisits,
.cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--arrivals,
.cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--impliedCleansInWindow {
	text-align: left !important;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--demandVisits,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--arrivals,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--impliedCleansInWindow,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--demandVisits,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--arrivals,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep tbody > tr > td.cwp-col--impliedCleansInWindow {
	text-align: right !important;
}

.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--demandVisits .v-data-table-header__content,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--arrivals .v-data-table-header__content,
.cleaning-work-plan--locale-rtl .cleaning-work-plan__data-table--rooms ::v-deep thead > tr > th.cwp-col--impliedCleansInWindow .v-data-table-header__content {
	justify-content: flex-start !important;
}

/* Column sizing: let Vuetify header.width control most sizing; force wrapping/ellipsis for long text */
.cleaning-work-plan__data-table--rooms ::v-deep .cwp-col--roomName {
	white-space: normal;
	word-break: break-word;
	overflow-wrap: break-word;
}

.cleaning-work-plan__data-table--rooms ::v-deep .cwp-col--building,
.cleaning-work-plan__data-table--rooms ::v-deep .cwp-col--floor,
.cleaning-work-plan__data-table--rooms ::v-deep .cwp-col--gender,
.cleaning-work-plan__data-table--rooms ::v-deep .cwp-col--demandVisits,
.cleaning-work-plan__data-table--rooms ::v-deep .cwp-col--arrivals,
.cleaning-work-plan__data-table--rooms ::v-deep .cwp-col--impliedCleansInWindow {
	white-space: nowrap;
}

.cleaning-work-plan__data-table ::v-deep thead th {
	font-size: 11px !important;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	white-space: nowrap;
	background: rgba(0, 0, 0, 0.04) !important;
}

.cleaning-work-plan--dark .cleaning-work-plan__data-table ::v-deep thead th {
	background: rgba(255, 255, 255, 0.08) !important;
}

/* Primary-tint hover when theme CSS vars exist; neutral fallback otherwise */
.cleaning-work-plan__data-table ::v-deep tbody tr:hover {
	background: rgba(0, 0, 0, 0.04) !important;
}

.cleaning-work-plan--dark .cleaning-work-plan__data-table ::v-deep tbody tr:hover {
	background: rgba(255, 255, 255, 0.06) !important;
}

@supports (background: color-mix(in srgb, red 12%, transparent)) {
	.cleaning-work-plan__data-table ::v-deep tbody tr:hover {
		background: color-mix(in srgb, var(--v-primary-base, #1976d2) 12%, transparent) !important;
	}

	.cleaning-work-plan--dark .cleaning-work-plan__data-table ::v-deep tbody tr:hover {
		background: color-mix(in srgb, var(--v-primary-base, #90caf9) 18%, transparent) !important;
	}
}

.cleaning-work-plan__data-table ::v-deep .v-data-table__mobile-row {
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.cleaning-work-plan--dark .cleaning-work-plan__data-table ::v-deep .v-data-table__mobile-row {
	border-bottom-color: rgba(255, 255, 255, 0.12);
}

.cleaning-work-plan__num {
	font-variant-numeric: tabular-nums;
}

.cleaning-work-plan__num--emphasis {
	font-weight: 600;
}

.cleaning-work-plan__floors-cell {
	white-space: normal;
	word-break: break-word;
	line-height: 1.35;
}

.cleaning-work-plan__ai-summary {
	white-space: pre-wrap;
	word-break: break-word;
	font-family: 'Courier New', Courier, monospace;
	font-size: 14px;
	line-height: 1.5;
	background: rgba(0, 0, 0, 0.04);
	padding: 16px;
	border-radius: 6px;
	overflow-x: auto;
}

.cleaning-work-plan--dark .cleaning-work-plan__ai-summary {
	background: rgba(255, 255, 255, 0.06);
}

.cleaning-work-plan__ai-status {
	background: rgba(0, 0, 0, 0.02);
}

.cleaning-work-plan--dark .cleaning-work-plan__ai-status {
	background: rgba(255, 255, 255, 0.04);
}

.cleaning-work-plan__ai-status-head {
	min-height: 24px;
}
</style>
